import { LinearProgress } from '@mui/joy';

import { Dispatch, SetStateAction } from 'react';

import { EXPERIMENT_METADATA_PATH_LOCAL_STORAGE_PREFIX } from '@/constants/datasetConstants';
import { ComparisonViewColumn, SessionSchema } from '@/types/schema';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { DatasetSessionCompareBarChartSpec } from '../../constants';
import { ChartsHeader } from '../ChartsHeader';
import { DatasetSessionCompareChart } from './DatasetSessionCompareChart';

export function DatasetSessionCompareBarCharts({
  columns,
  rows,
  selectedCharts,
  loadingCharts,
  datasetId,
  aggregateMetadataKeys,
  setHoveredSession,
  selectedBaselineSessionId,
  barChartSpecs,
  setSelectedCharts,
}: {
  columns: ComparisonViewColumn[];
  rows: SessionSchema[];
  selectedCharts: DatasetSessionCompareBarChartSpec[];
  loadingCharts: boolean;
  aggregateMetadataKeys: string[];
  setHoveredSession: (session: number | null) => void;
  selectedBaselineSessionId?: string | null;
  barChartSpecs: DatasetSessionCompareBarChartSpec[] | null;
  datasetId?: string;
  setSelectedCharts: Dispatch<
    SetStateAction<DatasetSessionCompareBarChartSpec[]>
  >;
}) {
  const [experimentMetadataPath, setExperimentMetadataPath] =
    useLocalStorageState<string | undefined>(
      `${EXPERIMENT_METADATA_PATH_LOCAL_STORAGE_PREFIX}${datasetId}`,
      undefined
    );

  return (
    <div className="sticky left-0 h-full overflow-auto bg-secondary pt-4">
      {loadingCharts ? (
        <div className="h-[430px]">
          <LinearProgress />
        </div>
      ) : (
        <div className="ml-4 flex flex-col gap-2 pb-10">
          <ChartsHeader
            barChartSpecs={barChartSpecs ?? []}
            selectedCharts={selectedCharts ?? []}
            setSelectedCharts={setSelectedCharts}
            aggregateMetadataKeys={aggregateMetadataKeys}
            experimentMetadataPath={experimentMetadataPath}
            setExperimentMetadataPath={setExperimentMetadataPath}
          />
          {columns.length > 1 && selectedCharts && selectedCharts.length > 0 ? (
            <DatasetSessionCompareBarChartsContent
              aggregateMetadataKeys={aggregateMetadataKeys}
              rowData={rows}
              selectedCharts={selectedCharts ?? []}
              chartGroupSpecs={barChartSpecs ?? []}
              setHoveredSession={setHoveredSession}
              selectedBaselineSessionId={selectedBaselineSessionId}
              experimentMetadataPath={experimentMetadataPath}
              setExperimentMetadataPath={setExperimentMetadataPath}
            />
          ) : (
            <div className="text-sm font-medium text-secondary">
              No charts selected
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DatasetSessionCompareBarChartsContent({
  rowData,
  aggregateMetadataKeys,
  chartGroupSpecs,
  selectedCharts,
  setHoveredSession,
  selectedBaselineSessionId,
  experimentMetadataPath,
  setExperimentMetadataPath,
}: {
  rowData: SessionSchema[];
  aggregateMetadataKeys?: string[];
  chartGroupSpecs: DatasetSessionCompareBarChartSpec[];
  selectedCharts: DatasetSessionCompareBarChartSpec[];
  setHoveredSession: (session: number | null) => void;
  selectedBaselineSessionId?: string | null;
  experimentMetadataPath?: string;
  setExperimentMetadataPath: (path: string | undefined) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {chartGroupSpecs
        .filter((spec) =>
          selectedCharts.some(
            (chart) =>
              chart.name === spec.name &&
              chart.isFeedback === spec.isFeedback &&
              chart.isSessionLevelFeedback === spec.isSessionLevelFeedback
          )
        )
        .map((spec, idx) => (
          <DatasetSessionCompareChart
            rowData={rowData}
            accessorFn={spec.accessor}
            yAxisLabel={spec.yAxisLabel}
            isLatency={spec.isLatency}
            latencyType={spec.latencyType}
            isErrorRate={spec.isErrorRate}
            isFeedback={spec.isFeedback}
            isSessionLevelFeedback={spec.isSessionLevelFeedback}
            feedbackKey={spec.feedbackKey}
            chartGroupTitle={spec.name}
            setHoveredSession={setHoveredSession}
            minimumValue={spec.minimumValue}
            maximumValue={spec.maximumValue}
            key={idx}
            selectedBaselineSessionId={selectedBaselineSessionId}
            aggregateMetadataKeys={aggregateMetadataKeys}
            experimentMetadataPath={experimentMetadataPath}
            setExperimentMetadataPath={setExperimentMetadataPath}
          />
        ))}
    </div>
  );
}
